import { Inject, Injectable } from '@angular/core';
import { ListingCategories } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, APPLICATION_NAME_TOKEN, ApplicationName, NilePaConstants, OutputDesignation } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiStoreConfiguration, AppStoreConfig, DataServiceApiConstants, PasDataServiceApiConstants, PasFeature, PasRcResolverService, PortfolioAnalysisOptions, PortfolioAnalysisParams, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams, ApiParamPortfolio, ApplicationNames, BaseHolding, Engagement, WweApiParamPositionImpl } from '@ddt-web/domain';
import { SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { AssetResolveResponse, PasApiService, PasPortfolioPosition } from '@ddt-web/pas';
import { ApplicationStateStoreProxyService, BpasEngagement, isBpasEngagement } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { SecurityResolutionDetailsRequest, WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import { Observable, of, zip } from 'rxjs';
import { catchError,first, map, switchMap, take } from 'rxjs/operators';

import { EngagementsUtil } from '../../utils/engagements.util';
import { PasPortfolioPositionsUtil } from '../../utils/pas-portfolio-positions.util';
import { PortfolioPositionsUtil } from '../../utils/portfolio-positions.util';
import { PasCustomParams } from '../models/pas-processing.interface';
import { PasApiProcessingService } from './api-processing/pas-api-processing.service';
import { PasDataPortfolioAnalysisService } from './pas-data-portfolio-analysis.service';
import { PasPostProcessingService } from './post-processing';
import { PasProxyProcessingService } from './post-processing/pas-proxy-processing.service';
import { TaxResponseEnricher } from './response-enrichment/tax-response-enricher';
import { PasResponseProcessingService } from './response-processing/pas-response-processing.service';

@Injectable()
export class PasDataLoaderService {
	public configArray: any;

	constructor(
		private dataPortfolioAnalysisService: PasDataPortfolioAnalysisService,
		private configService: ConfigService,
		private storeService: StoreService,
		private pasApiService: PasApiService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private pasApiProcessingService: PasApiProcessingService,
		private pasResponseProcessingService: PasResponseProcessingService,
		private pasProxyProcessingService: PasProxyProcessingService,
		protected pasRcResolver: PasRcResolverService,
		@Inject(APPLICATION_NAME_TOKEN) protected applicationName: ApplicationName,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public populateStoreForEngagement(
		engagementPortfolio: BpasEngagement | Engagement,
		associatedPortfolios: any,
		modelsConfig: any,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions,
		customConfigArray?: ApiStoreConfiguration[],
		additionalAnalysisParams?: AdditionalAnalysisParams
	): string[] {
		const ids = [];
		let benchmark: ApiParamPortfolio;
		const analysisParamsArray = new Array<PortfolioAnalysisParams>();
		this.initializeConfigArray(ids, customConfigArray);

		// for each of the children, create PortfolioAnalysisParams object and currentPortfolio to be added to associatedPortfolios
		(engagementPortfolio?.['children'] ?? []).map(port => {
			// IS THIS LOGIC CORRECT? will associatedPortfolio have model id instead of portfolio id mapping - only can replicate with portfolio id
			const portfolio = associatedPortfolios.entities[EngagementsUtil.getEngagementPortfolioId(port, modelsConfig)];

			const currency = isBpasEngagement(engagementPortfolio) ? engagementPortfolio.currency : this.resolveCurrency(additionalAnalysisParams?.currency);
			const isModelPortfolio = EngagementsUtil.isModel(port, modelsConfig);
			const isApbBenchmark = portfolio.outputDesignation === OutputDesignation.BENCHMARK;

			if (isModelPortfolio) {
				const modelRequestId = portfolio.portKey; // request id always have to be the portKey for mapping reasons
				const modelId = EngagementsUtil.getEngagementModelApcId(modelRequestId, modelsConfig);
				ids.push(modelRequestId);

				const modelPositions = [new WweApiParamPositionImpl(modelId, { modelId }, 100, 100)]; // do we need the value here as well?
				analysisParamsArray.push(new PortfolioAnalysisParams(modelRequestId, null, modelPositions, portfolio.description, currency));

				if (isApbBenchmark) {
					const modelPositions = [new WweApiParamPositionImpl('benchmark-' + modelId, { modelId }, 100, 100)]; // do we need the value here as well?
					benchmark = new ApiParamPortfolio(modelRequestId, modelPositions, ListingCategories.MODEL_PORTFOLIO, currency);
				}
			} else if (portfolio.positions) {
				ids.push(portfolio.id);

				const securityKeyIdentifier = PasPortfolioPositionsUtil.getSecurityKeyIdentifier(portfolio.positions, additionalAnalysisParams);
				analysisParamsArray.push(
					new PortfolioAnalysisParams(
						portfolio.id,
						null,
						PortfolioPositionsUtil.createWweParamPositions(portfolio.positions, '', securityKeyIdentifier),
						portfolio.description,
						currency
					)
				);

				if (isApbBenchmark) {
					benchmark = new ApiParamPortfolio(
						portfolio.id,
						PortfolioPositionsUtil.createWweParamPositions(portfolio.positions, 'benchmark-'),
						portfolio?.portfolioType || ListingCategories.USER_PORTFOLIO,
						currency
					);
				}
			}
		});

		// BPAS benchmark case
		if (isBpasEngagement(engagementPortfolio)) {
			if (!benchmark && engagementPortfolio?.benchmarkDetails?.benchmarkId) {
				const benchmarkForEngagement = engagementPortfolio.benchmarkDetails;
				let benchmarkPositions;
				if (!has(benchmarkForEngagement, 'positions')) {
					const foundBenchmarkPortfolio = get(engagementPortfolio, 'children', []).filter(port => port.uniqueId === benchmarkForEngagement.benchmarkId);
					if (!isEmpty(foundBenchmarkPortfolio)) {
						benchmarkPositions = get(foundBenchmarkPortfolio[0], 'children', []);
					} else {
						throw new Error('Could not find Benchmark as Portfolio');
					}
				} else {
					benchmarkPositions = get(benchmarkForEngagement, 'positions');
				}

				benchmark = new ApiParamPortfolio(
					benchmarkForEngagement.benchmarkId,
					PortfolioPositionsUtil.createWweParamPositions(benchmarkPositions, 'benchmark-'),
					ListingCategories.USER_PORTFOLIO,
					engagementPortfolio.currency
				);

				this.applicationStateStoreProxyService.updateAnalysisState({
					selectedCurrency: engagementPortfolio.currency
				});
			}
		}

		// call method to populate details, risk and performance and pass the AnalysisParams object
		this.dataPortfolioAnalysisService.loadPortfolioAnalysisData(
			this.configArray.filter(config => !!config),
			new AnalysisParams(analysisParamsArray, benchmark, null, additionalAnalysisParams?.simulatedPerformanceUserInputs)
		);

		return ids;
	}

	/**
	 * Method needed for PAS analysis as position data doesn't come back in the portfolio-analysis calls.
	 * The steps in populating the position details:
	 * 1. building the request body (unless provided) and making the API call
	 * 2. manipulates the response - mapping the securities to a portfolio (per portfolio analyzed vs transient analysis)
	 * 3. update store with the portfolios to load
	 * 4. post-processing - if provided; by default, proxy processing for Nile
	 * 5. feature - provide if required for custom error throwing in PasApiService fetchSecurityDetails
	 *
	 * @param portfolioIds
	 * @param throwSecurityDetailsPartialPortfoliosErrors
	 * @param additionalAnalysisParams
	 * @param pasCustomParams
	 * @param postProcessing
	 */
	public populateStoresForPositions(
		portfolioIds: string[],
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		pasCustomParams?: PasCustomParams,
		postProcessing: PasPostProcessingService[] = [this.pasProxyProcessingService], // TO BE UPDATED // default for Nile
		feature?: PasFeature,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): void {
		this.getPositionDetails(portfolioIds, throwSecurityDetailsPartialPortfoliosErrors, additionalAnalysisParams, pasCustomParams, feature, positionDetailsType)
			.pipe(take(1))
			.subscribe(([portfoliosToLoad, positionDataFromApiCall]) => {
				// update store
				this.positionsPostProcessing(portfoliosToLoad, positionDataFromApiCall, portfolioIds, postProcessing, feature, positionDetailsType);
			});
	}

	public populatePositionDetailsForModels(body: SecurityResolutionDetailsRequest, familyId: string) {
		this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS).pipe(
			map((rc) => ({
				body: body, rc
			} as WweSecurityDetailsParams)),
			switchMap((payload: WweSecurityDetailsParams) => this.pasApiService.fetchSecurityDetails(payload))
		).pipe(first())
			.subscribe((response: AssetResolveResponse) => {
				this.storeService.upsertMany(this.storeConfig.positionDetails, [{ id: familyId, children: Array.from(response?.assets?.values()) }]);
			});
	}

	/**
	 * Populates position details for model holdings by fetching security details and mapping them to the model portfolios.
	 *
	 * @param {SecurityResolutionDetailsRequest} body - The request body containing security resolution details.
	 * @param {any[]} modelPortfolios - An array of model portfolios to populate position details for.
	 * @param {string} locale - Optional locale parameter for the RC resolution.
	 *
	 * @returns {Observable<boolean>} - Returns true if positionDetails store is updated successfully, false in case of exception.
	 *
	 * This method performs the following steps:
	 * 1. Retrieves the resolution context (RC) for the user type using `PasDataServiceApiConstants.SECURITY_DETAILS`.
	 * 2. Constructs a payload with the request body and RC.
	 * 3. Fetches security details using the constructed payload.
	 * 4. Processes the response and maps the asset values to the corresponding model portfolios and their components.
	 * 5. Updates the store with the populated position details for the model portfolios.
	 * 6. Returns true if successful, false if an exception occurs.
	 */
	public populatePositionDetailsForModelsHoldings(body: SecurityResolutionDetailsRequest, modelPortfolios: any[], locale?: string): Observable<boolean> {
		return this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.SECURITY_DETAILS, locale).pipe(
			map((rc) => ({
				body: body, rc
			} as WweSecurityDetailsParams)),
			switchMap((payload: WweSecurityDetailsParams) => this.pasApiService.fetchSecurityDetails(payload)),
			first(),
			map((response: AssetResolveResponse) => {
				try {
					const assetValues = Array.from(response?.assets?.values());
					const modelsPositionDetails = modelPortfolios.map(modelPortfolio => {
						const modelPosition = { id: modelPortfolio.id, children: [] };
						modelPortfolio.components.forEach(component => {
							assetValues.forEach(assetValue => {
								if (assetValue.ticker === component.posInfo?.ticker?.stringValue || assetValue.assetId === component.assetId) {
									modelPosition.children.push(assetValue);
								}
							});
						});
						return modelPosition;
					});
					this.storeService.upsertMany(this.storeConfig.positionDetails, modelsPositionDetails);
					return true;
				} catch (error) {
					console.error('Error updating position details store:', error);
					return false;
				}
			}),
			catchError((error) => {
				console.error('Error in populatePositionDetailsForModelsHoldings:', error);
				return of(false);
			})
		);
	}

	public getPositionDetails(
		portfolioIds: string[],
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		pasCustomParams?: PasCustomParams,
		feature?: PasFeature,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): Observable<any> {
		portfolioIds = this.filterStoresPrefixesPortfolioIds(portfolioIds);
		// pre request - get correct api request & responses
		return this.pasApiProcessingService
			.getPositionDetailsResponse(
				portfolioIds,
				throwSecurityDetailsPartialPortfoliosErrors,
				additionalAnalysisParams,
				pasCustomParams?.customSecurityBody,
				feature,
				positionDetailsType,
				pasCustomParams?.transientAnalysisId
			)
			.pipe(
				switchMap(positionDataFromApiCall => {
					// set ids used to map back the response
					const ids = pasCustomParams?.transientAnalysisId ? [pasCustomParams?.transientAnalysisId] : portfolioIds;

					// custom response manipulation if needed
					positionDataFromApiCall = this.enrichResponse(positionDataFromApiCall, positionDetailsType);

					return zip(
						// return response + other api responses needed
						this.pasResponseProcessingService.getPortfoliosToLoad(
							ids,
							positionDataFromApiCall,
							throwSecurityDetailsPartialPortfoliosErrors,
							additionalAnalysisParams,
							positionDetailsType
						),
						of(positionDataFromApiCall)
					);
				}),
				take(1)
			);
	}

	public positionsPostProcessing(
		portfoliosToLoad: PasPortfolioPosition[],
		positionDataFromApiCall: AssetResolveResponse,
		ids: string[],
		postProcessing: PasPostProcessingService[] = [this.pasProxyProcessingService],
		feature?: PasFeature,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): void {
		ids = this.filterStoresPrefixesPortfolioIds(ids);
		// update store
		this.loadPositionData(portfoliosToLoad, feature, positionDetailsType);
		const store = PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType] || this.storeConfig.positionDetails;

		// post request actions
		postProcessing?.forEach(service => {
			service?.process(portfoliosToLoad, ids, positionDataFromApiCall, feature, store);
		});
	}

	public loadPositionData(
		mappedPortfolios: PasPortfolioPosition[],
		feature?: PasFeature,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): void {
		const store = this.storeConfig[PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType]] || this.storeConfig.positionDetails;
		switch (feature) {
			case PasFeature.COMPARE:
				this.storeService.upsertMany(this.storeConfig.positionDetailsCompare, mappedPortfolios);
				break;
			case PasFeature.FUNDS_TO_EXPLORE:
				this.storeService.upsertMany({ name: 'fundsToExplore', storeAsList: true }, mappedPortfolios);
				break;
			default:
				this.storeService.upsertMany(store, mappedPortfolios);
		}
	}

	public populateStoresForPortfolios(
		ids: any[],
		modelsConfig: any,
		associatedPortfolios: any = { entities: [] },
		additionalAnalysisParams: AdditionalAnalysisParams,
		benchmarkId?: string,
		customConfigArray?: ApiStoreConfiguration[],
		skipPerformancePerPortfolio?: boolean,
		shouldPersistPreviousPerformanceInStore?: boolean,
		pasCustomParams?: PasCustomParams
	): void {
		const filteredIds = this.filterStoresPrefixesPortfolioIds(ids);
		this.initializeConfigArray(filteredIds, customConfigArray);


		if (!Array.isArray(additionalAnalysisParams?.benchmark) && additionalAnalysisParams?.benchmark?.portfolioBenchmarkId) {
			this.storeService
				.selectAll(this.storeConfig.associatedPortfolios)
				.pipe(first())
				.subscribe(associatedPortfolios => {
					this.pasApiProcessingService.setupAnalysisParamsSubscription(
						filteredIds,
						this.configArray,
						associatedPortfolios,
						modelsConfig,
						additionalAnalysisParams,
						pasCustomParams
					);
				});
		} else {
			this.pasApiProcessingService.setupAnalysisParamsSubscription(
				filteredIds,
				this.configArray,
				associatedPortfolios,
				modelsConfig,
				additionalAnalysisParams,
				pasCustomParams
			);
		}
	}

	/**
	 * Creates a new portfolio with a single holding with weight 100% for each BaseHolding in the funds input array
	 * Makes an API call with all of these portfolios to populate the storeConfig
	 * Used in IQ to populate the fundAnalytics store from total-portfolios
	 */
	public populateStoresForSingleFunds(funds: Array<BaseHolding>, additionalAnalysisParams: AdditionalAnalysisParams, storeConfig: any): void {
		const portfolioAnalysisParams: Array<PortfolioAnalysisParams> = funds.map(holding => {
			const id =
				additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId && !!holding[additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId]
					? holding[additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId]
					: SecurityIdentifierUtil.buildSecurityAggregateKey(holding);
			const position: Array<WweApiParamPositionImpl> = PortfolioPositionsUtil.createWweParamPositions(
				[{ ...holding, weight: 100 }],
				'',
				additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId
			);

			return new PortfolioAnalysisParams(id, null, position, null, additionalAnalysisParams.currency);
		});

		const generalAnalysisParams: AnalysisParams = new AnalysisParams(portfolioAnalysisParams, null);
		this.dataPortfolioAnalysisService.loadPortfolioAnalysisData([storeConfig], generalAnalysisParams);
	}

	/**
	 * Enriches response based on the provided positionDetailsType.
	 * If no customisation is implemented, the provided positionDataFromApiCall is returned with no changes.
	 *
	 * @param positionDataFromApiCall
	 * @param positionDetailsType
	 * @returns
	 */
	private enrichResponse(positionDataFromApiCall: any, positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS): AssetResolveResponse {
		if (positionDetailsType === PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS_TAX) {
			return TaxResponseEnricher.enrichSecurityTax(positionDataFromApiCall);
		}

		return positionDataFromApiCall;
	}

	private filterStoresPrefixesPortfolioIds(portfolioIds: Array<string>): Array<string> {
		// extra check for asset class, rebalance and latest ids
		return portfolioIds.filter(id => !Object.values(DataServiceApiConstants.STORES_PREFIXES).some(prefix => id.startsWith(prefix)));
	}

	protected initializeConfigArray(ids: any[], customConfigArray?: ApiStoreConfiguration[]): void {
		switch (this.applicationName) {
			case (ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION):
			case (ApplicationNames.PROFESSIONAL_INVESTORS):
			case (ApplicationNames.PORTFOLIO_CENTRE):
			case (ApplicationNames.PORTFOLIO_CENTRE_360):
				this.configArray = [this.storeConfig.risk, this.storeConfig.performance, this.storeConfig.details];
				break;
			case (ApplicationNames.AC_PORTFOLIO_LIBRARY):
				this.configArray = customConfigArray ?? [
					this.storeConfig.risk, this.storeConfig.performance, this.storeConfig.performanceExBenchmark, this.storeConfig.details, this.storeConfig.rules
				];
				break;
			case ApplicationNames.CMA_ESTIMATOR:
				this.configArray = customConfigArray ?? [this.storeConfig.wealthProjections, this.storeConfig.risk, this.storeConfig.details]; // performance APIs are triggered separately when step-3 is reached with valid strategy
				break;
			case ApplicationNames.PORTFOLIO_ANALYSER:
				this.configArray = customConfigArray || [this.storeConfig.risk, this.storeConfig.details, this.storeConfig.esg, this.storeConfig.wealthProjections];
				break;
			case (ApplicationNames.SCENARIO_TESTER):
				this.configArray = customConfigArray || [this.storeConfig.risk, this.storeConfig.scenarioTesterDetails]; // customConfigArray used for optimisation when current analysis is edited  
				break;
			case ApplicationNames.INVESTMENT_QUALITY:
				this.configArray = !isEmpty(customConfigArray) ? [] : [this.storeConfig.details];
		}
	}

	private resolveCurrency(currency: string): string {
		return this.configService.get<string>(SharedConfigKey.PC_CURRENCY_SELECTION_OPTIONS, NilePaConstants.DEFAULT_CURRENCIES).split(',').includes(currency) ? currency : null;
	}
}
